/* eslint-disable max-len */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { ProductResultsContext } from '../components/ProductResultsContext';
import {
  generateDeliveryOptions,
  areAnyRefinementsEligibleInDimensions,
  isTodayOrByTomorrowEligibleInSomeDimensions
} from '../utils/deliveryUtils';
import {
  DELIVERY_REFINEMENTS,
  FULFILLMENT_DIMENSIONS_LABELS,
  BROWSE_ALL_REFINEMENT,
  FULFILLMENT_LABELS
} from '../utils/constants';
import {
  isPlpDimensionLabel,
  resetDimensionLabel,
  getFulfillmentTileLabel,
  createFulfillmentDimension
} from '../utils/fulfillment-helpers';
import { getAppliedRefinements } from '../product-results-helpers';

const { node } = PropTypes;

/**
 * @typedef {object} DeliveryOptionsContextValue
 * @property {string} selectedDeliveryDimension - The currently selected delivery dimension (e.g., 'delivery', 'freeDelivery').
 * @property {Function} setSelectedDeliveryDimension - Function to update the selected delivery dimension.
 * @property {boolean} isFreeDeliveryChecked - Indicates if the "Free Delivery" option is selected.
 * @property {Function} setIsFreeDeliveryChecked - Function to toggle the "Free Delivery" option.
 * @property {Array} deliveryOptions - The available delivery options based on the current dimensions.
 * @property {boolean} freeDeliveryUnavailable - Indicates if "Free Delivery" is unavailable.
 * @property {boolean} hideDeliveryOptions - Indicates if delivery options should be hidden.
 * @property {Array} dimensions - The dimensions used to generate delivery options.
 * @property {string|null} triggerDeliverySpeedChange - The current delivery speed change trigger (e.g., 'Anytime', 'Today', 'By Tomorrow', or null).
 * @property {Function} setTriggerDeliverySpeedChange - Function to set the delivery speed change trigger.
 */

/**
 * Context for handling common Delivery Options values.
 *
 * @type {React.Context<DeliveryOptionsContextValue>}
 */
const DeliveryOptionsContext = createContext();

export const DeliveryOptionsProvider = ({ children }) => {
  const { data } = useContext(ProductResultsContext);
  const { dimensions = [], appliedDimensions = [] } = data?.searchModel || {};
  const fulfillmentDimensions = createFulfillmentDimension(dimensions);
  const appliedRefinements = useMemo(() => getAppliedRefinements(appliedDimensions), [appliedDimensions]);

  // Keep track of the applied fulfillment dimension, including label, and refinement key
  const appliedFulfillmentDimension = useMemo(
    () => appliedRefinements?.find((dim) => isPlpDimensionLabel(dim?.dimensionName, true)),
    [appliedRefinements]
  );
  const appliedDimensionLabel = useMemo(
    () => resetDimensionLabel(
      appliedFulfillmentDimension?.dimensionName, appliedFulfillmentDimension?.refinementLabel, true
    ) ?? BROWSE_ALL_REFINEMENT.label,
    [appliedFulfillmentDimension]
  );
  const appliedRefinementKey = useMemo(
    () => appliedFulfillmentDimension?.refinementKey ?? BROWSE_ALL_REFINEMENT.refinementKey, [appliedFulfillmentDimension]
  );
  const [selectedFulfillmentDimensionLabel, setSelectedFulfillmentDimensionLabel] = useState(getFulfillmentTileLabel(appliedDimensionLabel));

  // State to manage delivery options and selections
  const [selectedDeliveryDimension, setSelectedDeliveryDimension] = useState('delivery');
  const [isFreeDeliveryChecked, setIsFreeDeliveryChecked] = useState(
    appliedFulfillmentDimension?.dimensionName === FULFILLMENT_LABELS.FREE_DELIVERY
  );
  const [selectedDeliverySpeed, setSelectedDeliverySpeed] = useState(
    /delivery/i.test(appliedFulfillmentDimension?.dimensionName)
      ? appliedFulfillmentDimension?.refinementLabel : ''
  );
  const [triggerDeliverySpeedChange, setTriggerDeliverySpeedChange] = useState(null);

  // Flag to force sync local UI state with context state between FulfillmentOptions (Super Toggle)
  // and FulfillmentDimension (in the All filters Drawer)
  const [forceInitialSync, setForceInitialSync] = useState(false);

  const deliveryOptions = useMemo(
    () => generateDeliveryOptions(dimensions, FULFILLMENT_DIMENSIONS_LABELS[selectedDeliveryDimension]),
    [dimensions, selectedDeliveryDimension]
  );
  const freeDeliveryUnavailable = useMemo(() => !areAnyRefinementsEligibleInDimensions(
    dimensions,
    [FULFILLMENT_DIMENSIONS_LABELS.freeDelivery],
    [DELIVERY_REFINEMENTS.ANYTIME, DELIVERY_REFINEMENTS.TODAY, DELIVERY_REFINEMENTS.BY_TOMORROW]
  ), [dimensions]);
  const hideDeliveryOptions = useMemo(() => !isTodayOrByTomorrowEligibleInSomeDimensions(
    dimensions,
    [FULFILLMENT_DIMENSIONS_LABELS.delivery, FULFILLMENT_DIMENSIONS_LABELS.freeDelivery]
  ), [dimensions]);

  const value = useMemo(() => ({
    selectedDeliveryDimension,
    setSelectedDeliveryDimension,
    selectedDeliverySpeed,
    setSelectedDeliverySpeed,
    isFreeDeliveryChecked,
    setIsFreeDeliveryChecked,
    deliveryOptions,
    freeDeliveryUnavailable,
    hideDeliveryOptions,
    dimensions,
    appliedDimensions,
    appliedFulfillmentDimension,
    fulfillmentDimensions,
    triggerDeliverySpeedChange,
    setTriggerDeliverySpeedChange,
    selectedFulfillmentDimensionLabel,
    setSelectedFulfillmentDimensionLabel,
    appliedDimensionLabel,
    appliedRefinementKey,
    forceInitialSync,
    setForceInitialSync
  }), [
    selectedDeliveryDimension,
    setSelectedDeliveryDimension,
    selectedDeliverySpeed,
    setSelectedDeliverySpeed,
    isFreeDeliveryChecked,
    setIsFreeDeliveryChecked,
    deliveryOptions,
    freeDeliveryUnavailable,
    hideDeliveryOptions,
    dimensions,
    appliedDimensions,
    appliedFulfillmentDimension,
    fulfillmentDimensions,
    triggerDeliverySpeedChange,
    setTriggerDeliverySpeedChange,
    selectedFulfillmentDimensionLabel,
    setSelectedFulfillmentDimensionLabel,
    appliedDimensionLabel,
    appliedRefinementKey,
    forceInitialSync,
    setForceInitialSync
  ]);

  useEffect(() => {
    setIsFreeDeliveryChecked(appliedFulfillmentDimension?.dimensionName === FULFILLMENT_LABELS.FREE_DELIVERY);

    if (/delivery/i.test(appliedFulfillmentDimension?.dimensionName)) {
      setSelectedDeliverySpeed(appliedFulfillmentDimension?.refinementLabel);
    }
  }, [appliedFulfillmentDimension]);

  useEffect(() => {
    if (appliedDimensionLabel) {
      setSelectedFulfillmentDimensionLabel(getFulfillmentTileLabel(appliedDimensionLabel));
    }
  }, [appliedDimensionLabel]);

  return (
    <DeliveryOptionsContext.Provider
      value={value}
    >
      {children}
    </DeliveryOptionsContext.Provider>
  );
};

DeliveryOptionsProvider.propTypes = {
  children: node.isRequired
};

/**
 * Custom hook to access the DeliveryOptionsContext.
 * @returns {DeliveryOptionsContextValue} The current delivery options context value.
 */
export const useDeliveryOptionsContext = () => useContext(DeliveryOptionsContext);